/**
 * notifications.store — store Zustand do sininho de notificações.
 *
 * Spec §15:
 *   • Persistência IN-MEMORY apenas (perdida no reload). Não há storage no backend.
 *   • Rehidratação inicial via GET /executions?page=1&page_size=20 (responsabilidade
 *     de quem chama `add`/`hydrate`, não deste store).
 *   • Mantém no máximo 20 notificações — as mais recentes ficam no topo.
 *   • `unreadCount` é derivado/sincronizado a cada mutação.
 *
 * Alimentado por `useNotificationStream` (SSE global, AppShell): cada evento
 * `execution_end` chama `add()` com `read: false`. A rehidratação inicial
 * popula histórico marcado como `read: true`.
 */
import { create } from 'zustand'

import type { ExecutionStatus } from '@/types/enums'

/** Capacidade máxima da lista. */
export const NOTIFICATIONS_MAX = 20

export interface Notification {
  id: string
  type: 'execution_end'
  title: string
  status: ExecutionStatus
  execution_id: string
  /** ISO datetime. */
  timestamp: string
  read: boolean
}

export interface NotificationsState {
  notifications: Notification[]
  unreadCount: number
}

export interface NotificationsActions {
  /** Adiciona uma notificação no topo. Trunca em NOTIFICATIONS_MAX itens. */
  add: (notification: Notification) => void
  /** Substitui a lista inteira (rehidratação inicial pós-login/reload). */
  hydrate: (notifications: Notification[]) => void
  /** Marca todas como lidas e zera `unreadCount`. */
  markAllRead: () => void
  /** Limpa todas as notificações (logout). */
  clear: () => void
}

export type NotificationsStore = NotificationsState & NotificationsActions

function countUnread(list: Notification[]): number {
  let n = 0
  for (const item of list) if (!item.read) n++
  return n
}

export const useNotificationsStore = create<NotificationsStore>((set) => ({
  notifications: [],
  unreadCount: 0,

  add: (notification) =>
    set((state) => {
      const next = [notification, ...state.notifications].slice(0, NOTIFICATIONS_MAX)
      return { notifications: next, unreadCount: countUnread(next) }
    }),

  hydrate: (notifications) => {
    const next = notifications.slice(0, NOTIFICATIONS_MAX)
    set({ notifications: next, unreadCount: countUnread(next) })
  },

  markAllRead: () =>
    set((state) => ({
      notifications: state.notifications.map((n) => (n.read ? n : { ...n, read: true })),
      unreadCount: 0,
    })),

  clear: () => set({ notifications: [], unreadCount: 0 }),
}))
